import { useMemo } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { merge } from 'lodash'
import { ApolloError } from '@apollo/client'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { useGetPatientHistoryPrefillData } from './useGetPatientHistoryPrefillData'

type FormValuesState<T> = {
  values: T
  status: 'loading' | 'error' | 'initialized' | 'draft' | 'draftError'
}

export function useLocalStorageWithPrefillValues<T>(
  uid: string,
  formType: PatientHistoryType,
  getDefaultValues: () => T,
  callID?: string | null
): FormValuesState<T> {
  const { data: prefillData, loading, error } = useGetPatientHistoryPrefillData(uid, formType, callID)
  const loaded = useLoadedNoteDraft({ uid, type: formType })

  const result = useMemo(() => {
    if (loaded.failed) {
      return { values: getDefaultValues(), status: 'draftError' as const }
    }
    if (!loaded.ready) {
      return { values: getDefaultValues(), status: 'loading' as const }
    }

    if (loaded.draft) {
      return { values: loaded.draft as T, status: 'draft' as const }
    }

    const mergedValues = merge(getDefaultValues(), prefillData?.value)

    const status = getStatus(loading, error)

    return { values: mergedValues, status }
  }, [loaded.failed, loaded.ready, loaded.draft, getDefaultValues, prefillData?.value, loading, error])

  return result
}

function getStatus(loading: boolean, error: ApolloError | undefined) {
  if (loading) {
    return 'loading' as const
  }
  if (error) {
    return 'error' as const
  }
  return 'initialized' as const
}
